import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { socialLinks } from '../data/mock';
import { MessageCircle, Images, Check } from 'lucide-react';
import useMediaQuery from '../hooks/useMediaQuery';

function MobileHero() {
    const { t, isRTL } = useLanguage();

    const scrollToGallery = () => {
        const element = document.getElementById('gallery');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <section id='home' className='bg-black mt-[75px] overflow-hidden'>
            {/* IMAGE / TITLE SECTION */}
            <div className='relative flex flex-shrink-0 justify-center items-end pt-44 pb-5'>
                {/* Background image */}
                <div className='absolute inset-0'>
                    <img
                        src='background.jpeg'
                        alt='Tattoo studio'
                        className='opacity-20 w-full h-full object-bottom object-cover'
                    />
                    <div className='absolute inset-0' />
                </div>

                {/* Title + Subtitle */}
                <div className='z-10 relative space-y-6 px-4 max-w-5xl text-center'>
                    <p
                        className={`${
                            isRTL && '-mb-3.5 text-xl'
                        } font-bold text-sm uppercase tracking-[0.2em]`}
                        style={{ fontFamily: isRTL ? 'Arial' : 'Space Mono' }}
                    >
                        {isRTL ? (
                            <div className='flex max-sm:flex-col justify-center'>
                                <div>כל קעקוע הוא סיפור,</div>
                                <div>כל קו הוא אמנות.</div>
                            </div>
                        ) : (
                            'Every Tattoo is a Story, Every Line is Art.'
                        )}
                    </p>

                    <h1
                        className='font-bold text-[45px] text-white sm:text-6xl lg:text-7xl tracking-wide'
                        style={{ fontFamily: 'Trajan' }}
                    >
                        {t.hero.title}
                    </h1>
                </div>
            </div>

            {/* BLACK CONTENT SECTION */}
            <div className='bg-black py-16'>
                <div className='space-y-8 mx-auto px-4 max-w-5xl text-center'>
                    {/* Features */}
                    <div className='space-y-2 text-mid-grey text-lg'>
                        <div className='flex justify-center items-center gap-2'>
                            <Check color='white' />
                            <span>
                                {isRTL
                                    ? 'אמנות קעקועים מובחרת'
                                    : 'Premium Tattoo Art'}
                            </span>
                        </div>

                        <div className='flex justify-center items-center gap-2'>
                            <Check color='white' />
                            <span>
                                {isRTL
                                    ? 'פירסינג מקצועי ועיצובים מותאמים אישית'
                                    : 'Professional Piercing and Custom Designs'}
                            </span>
                        </div>

                        <div className='flex justify-center items-center gap-2'>
                            <Check color='white' />
                            <span>
                                {isRTL
                                    ? 'הדמיית זקיקי שיער בשיטת מיקרו-פיגמנטציה'
                                    : 'Micro-Pigmentation Hair Follicle Simulation'}
                            </span>
                        </div>

                        <div className='font-bold text-white'>
                            {isRTL
                                ? 'הכל נעשה בסביבה סטרילית, נקייה ומזמינה'
                                : 'All done in a sterile, clean, and welcoming environment'}
                        </div>
                    </div>

                    {/* CTA Buttons */}
                    <div className='flex sm:flex-row flex-col justify-center items-center gap-4 pt-6'>
                        <a
                            href={socialLinks.whatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{ fontFamily: 'Arial' }}
                            className='group flex items-center gap-3 bg-mid-green hover:bg-mid-green/90 px-8 py-4 rounded-full text-black text-sm uppercase tracking-wider transition'
                        >
                            <MessageCircle className='w-5 h-5' />
                            {t.hero.ctaPrimary}
                        </a>

                        <button
                            onClick={scrollToGallery}
                            style={{ fontFamily: 'Arial' }}
                            className='group flex items-center gap-3 hover:bg-light-pink px-8 py-4 border border-light-pink rounded-full text-light-pink hover:text-black text-sm uppercase tracking-wider transition'
                        >
                            <Images className='w-5 h-5' />
                            {t.hero.ctaSecondary}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

function DesktopHero() {
    const { t, isRTL } = useLanguage();

    const scrollToGallery = () => {
        const element = document.getElementById('gallery');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <section
            id='home'
            className='relative flex justify-center items-center bg-black min-h-screen overflow-hidden'
        >
            {/* Background Image with Overlay */}
            <div className='absolute inset-0'>
                <img
                    src='background.jpeg'
                    alt='Tattoo studio'
                    className='opacity-40 w-full h-full object-cover'
                />
                <div className='absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black' />
            </div>

            {/* Decorative Elements */}
            <div className='top-20 left-10 absolute bg-mid-purple/20 blur-3xl rounded-full w-32 h-32 animate-pulse' />
            <div className='right-20 bottom-40 absolute bg-light-pink/15 blur-3xl rounded-full w-48 h-48 animate-pulse delay-1000' />

            {/* Content */}
            <div className='z-10 relative mx-auto px-4 max-w-5xl text-center'>
                <div className='space-y-8'>
                    {/* Subtitle */}
                    <p
                        className={`${
                            isRTL && '-mb-3.5 text-xl'
                        } font-bold text-sm uppercase tracking-[0.2em] animate-fade-in`}
                        style={{ fontFamily: isRTL ? 'Arial' : 'Space Mono' }}
                    >
                        {isRTL ? (
                            <div className='flex max-sm:flex-col justify-center'>
                                <div>כל קעקוע הוא סיפור,</div>
                                <div>כל קו הוא אמנות.</div>
                            </div>
                        ) : (
                            <div>
                                Every Tattoo is a Story, Every Line is Art.
                            </div>
                        )}
                    </p>

                    {/* Main Title */}
                    <h1
                        style={{ fontFamily: 'Trajan' }}
                        className='font-bold text-[45px] text-white sm:text-6xl lg:text-7xl leading-8 tracking-wide animate-slide-up'
                    >
                        {t.hero.title}
                    </h1>

                    {/* Description */}
                    <div className='space-y-2 mx-auto max-w-2xl text-mid-grey text-lg sm:text-xl leading-relaxed animate-fade-in-delay'>
                        <div className='flex flex-row justify-center items-center gap-2'>
                            <Check color='white' />
                            {isRTL ? (
                                <span>אמנות קעקועים מובחרת</span>
                            ) : (
                                <span>Premium Tattoo Art</span>
                            )}
                        </div>
                        <div className='flex flex-row justify-center items-center gap-2'>
                            <Check color='white' />
                            {isRTL ? (
                                <span>
                                    פירסינג מקצועי ועיצובים מותאמים אישית
                                </span>
                            ) : (
                                <span>
                                    Professional Piercing and Custom Designs
                                </span>
                            )}
                        </div>
                        <div className='flex flex-row justify-center items-center gap-2'>
                            <Check color='white' />
                            {isRTL ? (
                                <span>
                                    הדמיית זקיקי שיער בשיטת מיקרו-פיגמנטציה
                                </span>
                            ) : (
                                <span>
                                    Micro-Pigmentation Hair Follicle Simulation
                                </span>
                            )}
                        </div>
                        <div className='flex flex-row justify-center items-center gap-2 mt-1 font-bold text-white'>
                            {isRTL ? (
                                <span>
                                    הכל נעשה בסביבה סטרילית, נקייה ומזמינה
                                </span>
                            ) : (
                                <span>
                                    All done in a sterile, clean, and welcoming
                                    environment
                                </span>
                            )}
                        </div>
                    </div>

                    {/* CTA Buttons */}
                    <div className='flex sm:flex-row flex-col justify-center items-center gap-4 pt-6 animate-slide-up-delay'>
                        {/* Primary CTA - WhatsApp */}
                        <a
                            style={{ fontFamily: 'Arial' }}
                            href={socialLinks.whatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='group flex items-center gap-3 bg-mid-green hover:bg-mid-green/90 hover:shadow-lg hover:shadow-mid-green/25 px-8 py-4 rounded-full font-mono text-black text-sm uppercase tracking-wider hover:scale-105 transition-all duration-300'
                        >
                            <MessageCircle className='w-5 h-5 group-hover:scale-110 transition-transform' />
                            {t.hero.ctaPrimary}
                        </a>

                        {/* Secondary CTA - Gallery */}
                        <button
                            style={{ fontFamily: 'Arial' }}
                            onClick={scrollToGallery}
                            className='group flex items-center gap-3 hover:bg-light-pink px-8 py-4 border border-light-pink rounded-full font-mono text-light-pink hover:text-black text-sm uppercase tracking-wider transition-all duration-300'
                        >
                            <Images className='w-5 h-5 group-hover:scale-110 transition-transform' />
                            {t.hero.ctaSecondary}
                        </button>
                    </div>
                </div>
            </div>

            {/* Scroll Indicator */}
            <div className='bottom-8 left-1/2 absolute -translate-x-1/2 animate-bounce'>
                <div className='flex justify-center items-start p-2 border-2 border-light-pink/50 rounded-full w-6 h-10'>
                    <div className='bg-light-pink rounded-full w-1 h-2 animate-scroll' />
                </div>
            </div>
        </section>
    );
}

const Hero = () => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    return isMobile ? <MobileHero /> : <DesktopHero />;
};

export default Hero;
